import * as Ramda from 'ramda';
import type { CustomizeQuoteState } from '../views/customize-quote/types';
import { isUnderwriteQuoteState } from '../views/customize-quote/types';
import type { EditQuoteState } from '../views/edit-quote/types';
import type { UnderwriteQuoteState } from '../views/underwriter/types';
import { isCoverageEndorsementsState } from '../views/underwriter/types';
import type { ProductType } from '../../../../../types';
import { ProductTypes } from '../../../../../types';
import type { UiSettings } from '../types';

export const deriveUiSettings = <T extends QuoteStateType>(
  data: T
): UiSettings => {
  if (!data.productType) {
    throw new Error('Product type is required');
  }
  const isUnderwriterState =
    isUnderwriteQuoteState(data) || isCoverageEndorsementsState(data);
  const isAdmittedPrimeXProduct = data.productType == ProductTypes.p100_pro;
  const isPrimeOneTech = data.productType == ProductTypes.prime_one_tech;

  return {
    showCoverageTerritorialLimit: isPrimeOneTech,
    showCoverageBasisOfCover: isPrimeOneTech,
    showCoverageWaitingPeriods: !isAdmittedPrimeXProduct,
    showCoverageRetroactiveDate: !isAdmittedPrimeXProduct,
    showGlobalWaitingPeriod: !isAdmittedPrimeXProduct,
    showCoverageEndDate: !isAdmittedPrimeXProduct || isUnderwriterState,
    showClassDescription: data.productType === ProductTypes.prime_tech,
    showRenewalToggle: [
      ProductTypes.prime_one,
      ProductTypes.p100_pro,
      ProductTypes.prime_tech,
    ].some(Ramda.equals(data.productType)),
    showCommissionSlider: shouldShowCommissionSlider(data.productType),
    showSectionCollapseToggle: data.productType === ProductTypes.zsp,
  };
};

const mergeUiSettingsIntoState = <T extends QuoteStateType>(data: T) => {
  const uiSettings = deriveUiSettings(data);

  return {
    ...data,
    uiSettings,
  };
};

export default mergeUiSettingsIntoState;

type QuoteStateType =
  | CustomizeQuoteState
  | Partial<EditQuoteState>
  | Partial<UnderwriteQuoteState>;

export function shouldShowCommissionSlider(productType: ProductType): boolean {
  return (
    productType === ProductTypes.prime_one ||
    productType === ProductTypes.prime_one_au
  );
}
